import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchAPI } from "../config/api";
import {
  TeamHeader,
  TeamPercentiles,
  TeamLoading,
  TeamError,
  TabNavigation,
  TeamContent,
  STAT_TYPES,
} from "../components/team";
import { DEFAULT_YEAR } from "../config/constants";

const enrichStats = (stats) => {
  if (!Array.isArray(stats)) return [];
  return stats.map((stat) => {
    const enriched = { ...stat };
    if (stat.year && !stat.Year) enriched.Year = stat.year;
    if (stat.Season && !stat.year) enriched.year = stat.Season;
    return enriched;
  });
};

export const TeamPage = () => {
  const { teamId } = useParams();
  const [teamData, setTeamData] = useState(null);
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHeaderReady, setIsHeaderReady] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(STAT_TYPES.BATTING);
  const [statData, setStatData] = useState({
    [STAT_TYPES.VALUE_BATTING]: [],
    [STAT_TYPES.VALUE_PITCHING]: [],
    [STAT_TYPES.SITUATIONAL_BATTING]: [],
    [STAT_TYPES.SITUATIONAL_PITCHING]: [],
    [STAT_TYPES.SPLITS_BATTING]: [],
    [STAT_TYPES.SPLITS_PITCHING]: [],
    [STAT_TYPES.BATTED_BALL_BATTING]: [],
    [STAT_TYPES.BATTED_BALL_PITCHING]: [],
    [STAT_TYPES.BASERUNNING]: [],
  });

  const fetchStatsForType = useCallback(async (statType) => {
    try {
      const response = await fetchAPI(
        `/api/leaderboards/${statType}/${encodeURIComponent(teamId)}?start_year=2021&end_year=${DEFAULT_YEAR}`
      );
      const sorted = enrichStats(response).sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
      return { [statType]: sorted };
    } catch (err) {
      console.error(`Error fetching ${statType} stats:`, err);
      return { [statType]: [] };
    }
  }, [teamId]);

  const fetchAllStats = useCallback(async () => {
    const statTypes = [
      STAT_TYPES.VALUE_BATTING,
      STAT_TYPES.VALUE_PITCHING,
      STAT_TYPES.SITUATIONAL_BATTING,
      STAT_TYPES.SITUATIONAL_PITCHING,
      STAT_TYPES.SPLITS_BATTING,
      STAT_TYPES.SPLITS_PITCHING,
      STAT_TYPES.BATTED_BALL_BATTING,
      STAT_TYPES.BATTED_BALL_PITCHING,
      STAT_TYPES.BASERUNNING,
    ];
    const results = await Promise.all(statTypes.map(type => fetchStatsForType(type)));
    setStatData(results.reduce((acc, result) => ({ ...acc, ...result }), {}));
  }, [fetchStatsForType]);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setIsLoading(true);
      try {
        const [teamResponse, gamesResponse] = await Promise.all([
          fetchAPI(`/api/team/${teamId}`, { signal: controller.signal }),
          fetchAPI(`/api/team/${teamId}/games`, { signal: controller.signal }).catch(() => []),
        ]);
        if (controller.signal.aborted) return;
        setTeamData(teamResponse);
        setGames(gamesResponse || []);
        await fetchAllStats();
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(e.message || "Failed to load team data");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [teamId, fetchAllStats]);

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (teamData?.batting_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.BATTING, label: "Team Batting" });
    }
    if (teamData?.pitching_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.PITCHING, label: "Team Pitching" });
    }
    if (teamData?.batting_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.LINEUP, label: "Lineup" });
    }
    if (teamData?.pitching_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.BULLPEN, label: "Bullpen" });
    }
    if (teamData?.roster?.length > 0) {
      tabs.push({ id: STAT_TYPES.ROSTER, label: "Roster" });
    }
    if (games?.length > 0) {
      tabs.push({ id: STAT_TYPES.GAMES, label: "Games" });
    }
    if (statData[STAT_TYPES.VALUE_BATTING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.VALUE_BATTING, label: "Value (Batting)" });
    }
    if (statData[STAT_TYPES.VALUE_PITCHING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.VALUE_PITCHING, label: "Value (Pitching)" });
    }
    if (statData[STAT_TYPES.SITUATIONAL_BATTING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.SITUATIONAL_BATTING, label: "Situational (Batting)" });
    }
    if (statData[STAT_TYPES.SITUATIONAL_PITCHING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.SITUATIONAL_PITCHING, label: "Situational (Pitching)" });
    }
    if (statData[STAT_TYPES.SPLITS_BATTING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.SPLITS_BATTING, label: "Splits (Batting)" });
    }
    if (statData[STAT_TYPES.SPLITS_PITCHING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.SPLITS_PITCHING, label: "Splits (Pitching)" });
    }
    if (statData[STAT_TYPES.BATTED_BALL_BATTING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.BATTED_BALL_BATTING, label: "Batted Ball (Batting)" });
    }
    if (statData[STAT_TYPES.BATTED_BALL_PITCHING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.BATTED_BALL_PITCHING, label: "Batted Ball (Pitching)" });
    }
    if (statData[STAT_TYPES.BASERUNNING]?.length > 0) {
      tabs.push({ id: STAT_TYPES.BASERUNNING, label: "Baserunning" });
    }
    return tabs;
  }, [teamData, games, statData]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);

  if (isLoading) return <TeamLoading />;
  if (error) return <TeamError error={error} />;
  if (!teamData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="relative z-10 container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        {!isHeaderReady && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <TeamHeader teamData={teamData} onReady={() => setIsHeaderReady(true)} />
            </div>
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
              <TeamPercentiles
                teamId={teamData.team_id}
                years={teamData.years_played}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={handleTabChange}
              availableTabs={availableTabs}
            />
            <TeamContent 
              activeTab={activeTab} 
              teamData={teamData} 
              games={games}
              teamId={teamData.team_id}
              years={teamData.years_played}
              statData={statData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
